/**
 * WorkAxis — shared motion primitives (Framer Motion).
 * Every primitive respects prefers-reduced-motion: content renders static and fully visible.
 */
import React, {useEffect, useRef, useState} from 'react';
import {
    animate,
    motion,
    useInView,
    useMotionValue,
    useReducedMotion,
    useScroll,
    useSpring,
    useTransform
} from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const canHover = () =>
    typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/** Fade + rise once the element scrolls into view. */
export function Reveal({as = 'div', delay = 0, y = 26, children, ...rest}) {
    const reduce = useReducedMotion();
    const Comp = motion[as];
    if (reduce) return <Comp {...rest}>{children}</Comp>;
    return (
        <Comp
            initial={{opacity: 0, y}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true, amount: 0.15}}
            transition={{duration: 0.75, ease: EASE, delay}}
            {...rest}
        >
            {children}
        </Comp>
    );
}

const staggerParent = (gap) => ({
    hidden: {},
    show: {transition: {staggerChildren: gap}}
});

const staggerChild = {
    hidden: {opacity: 0, y: 22},
    show: {opacity: 1, y: 0, transition: {duration: 0.7, ease: EASE}}
};

/** Container whose StaggerItem children reveal one after another. */
export function Stagger({as = 'div', gap = 0.09, children, ...rest}) {
    const reduce = useReducedMotion();
    const Comp = motion[as];
    if (reduce) return <Comp {...rest}>{children}</Comp>;
    return (
        <Comp variants={staggerParent(gap)} initial="hidden" whileInView="show"
              viewport={{once: true, amount: 0.15}} {...rest}>
            {children}
        </Comp>
    );
}

export function StaggerItem({as = 'div', children, ...rest}) {
    const reduce = useReducedMotion();
    const Comp = motion[as];
    if (reduce) return <Comp {...rest}>{children}</Comp>;
    return <Comp variants={staggerChild} {...rest}>{children}</Comp>;
}

/**
 * Card that reveals on scroll, tilts toward the pointer and carries a cursor spotlight
 * (exposed as --mx / --my for CSS).
 */
export function TiltCard({as = 'div', className = '', delay = 0, max = 2, children, style, ...rest}) {
    const reduce = useReducedMotion();
    const Comp = motion[as];
    const ref = useRef(null);
    const px = useMotionValue(0.5);
    const py = useMotionValue(0.5);
    const springCfg = {stiffness: 180, damping: 18, mass: 0.4};
    const rotateX = useSpring(useTransform(py, [0, 1], [max, -max]), springCfg);
    const rotateY = useSpring(useTransform(px, [0, 1], [-max, max]), springCfg);
    const mx = useTransform(px, (v) => `${v * 100}%`);
    const my = useTransform(py, (v) => `${v * 100}%`);

    const cls = `wx-tilt ${className}`.trim();

    if (reduce) {
        return <Comp className={cls} style={style} {...rest}>{children}</Comp>;
    }

    const onMove = (e) => {
        if (!canHover() || !ref.current) return;
        const r = ref.current.getBoundingClientRect();
        px.set((e.clientX - r.left) / r.width);
        py.set((e.clientY - r.top) / r.height);
    };
    const onLeave = () => {
        px.set(0.5);
        py.set(0.5);
    };

    return (
        <Comp
            ref={ref}
            className={cls}
            onPointerMove={onMove}
            onPointerLeave={onLeave}
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            whileHover={canHover() ? {y: -4} : undefined}
            viewport={{once: true, amount: 0.15}}
            transition={{duration: 0.75, ease: EASE, delay}}
            style={{...style, rotateX, rotateY, transformPerspective: 900, '--mx': mx, '--my': my}}
            {...rest}
        >
            {children}
        </Comp>
    );
}

/** Moves its child vertically relative to scroll. speed > 0 drifts up as you scroll down. */
export function Parallax({speed = 40, fade = false, className, children}) {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const {scrollYProgress} = useScroll({target: ref, offset: fade ? ['start start', 'end start'] : ['start end', 'end start']});
    const y = useTransform(scrollYProgress, [0, 1], fade ? [0, speed] : [speed, -speed]);
    // Fade only once the block is mostly scrolled past (so tall stacked heroes on phones stay crisp).
    const opacity = useTransform(scrollYProgress, [0, 0.65, 1], fade ? [1, 1, 0.15] : [1, 1, 1]);
    if (reduce) return <div ref={ref} className={className}>{children}</div>;
    return (
        <motion.div ref={ref} className={className} style={{y, opacity}}>
            {children}
        </motion.div>
    );
}

/** Counts from `from` to `to` once visible. */
export function CountUp({from = 0, to, duration = 1.6, suffix = '', className}) {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const inView = useInView(ref, {once: true, amount: 0.6});
    const [value, setValue] = useState(reduce ? to : from);

    useEffect(() => {
        if (!inView || reduce) return undefined;
        const controls = animate(from, to, {
            duration,
            ease: EASE,
            onUpdate: (v) => setValue(Math.round(v))
        });
        return () => controls.stop();
    }, [inView, reduce, from, to, duration]);

    return <span ref={ref} className={className}>{value}{suffix}</span>;
}

/** Thin gradient bar at the top of the viewport tracking page scroll. */
export function ScrollProgress() {
    const {scrollYProgress} = useScroll();
    const scaleX = useSpring(scrollYProgress, {stiffness: 140, damping: 26, restDelta: 0.001});
    return <motion.div className="wx-scroll-progress" style={{scaleX}} aria-hidden="true"/>;
}

/** Wrapper that pulls its child slightly toward the cursor. */
export function Magnetic({strength = 0.12, children, className}) {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const x = useSpring(0, {stiffness: 220, damping: 15, mass: 0.3});
    const y = useSpring(0, {stiffness: 220, damping: 15, mass: 0.3});

    if (reduce) return <span className={className}>{children}</span>;

    const onMove = (e) => {
        if (!canHover() || !ref.current) return;
        const r = ref.current.getBoundingClientRect();
        x.set((e.clientX - (r.left + r.width / 2)) * strength);
        y.set((e.clientY - (r.top + r.height / 2)) * strength);
    };
    const onLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.span ref={ref} className={`wx-magnetic ${className || ''}`.trim()}
                     onPointerMove={onMove} onPointerLeave={onLeave} style={{x, y}}>
            {children}
        </motion.span>
    );
}

/** Heading whose words rise in one by one. Accepts a string or a fragment of strings / elements. */
export function WordsReveal({as = 'h1', className, children, delay = 0}) {
    const reduce = useReducedMotion();
    const Comp = motion[as];
    if (reduce) return <Comp className={className}>{children}</Comp>;

    const parts = [];
    const pushText = (text, wrap) => {
        text.split(/(\s+)/).forEach((tok) => {
            if (!tok) return;
            if (/^\s+$/.test(tok)) parts.push(' ');
            else parts.push(wrap ? wrap(tok) : tok);
        });
    };
    const walk = (node) => {
        if (node == null || node === false) return;
        if (typeof node === 'string' || typeof node === 'number') {
            pushText(String(node));
        } else if (Array.isArray(node)) {
            node.forEach(walk);
        } else if (React.isValidElement(node)) {
            if (node.type === React.Fragment) {
                walk(node.props.children);
            } else if (typeof node.props.children === 'string') {
                pushText(node.props.children, (w) => React.cloneElement(node, {}, w));
            } else {
                parts.push(node);
            }
        }
    };
    walk(children);

    return (
        <Comp className={className} initial="hidden" animate="show"
              variants={{hidden: {}, show: {transition: {staggerChildren: 0.06, delayChildren: delay}}}}>
            {parts.map((p, i) => (p === ' ' ? ' ' : (
                <span className="wx-word" key={i}>
                    <motion.span className="wx-word-inner"
                                 variants={{
                                     hidden: {y: '110%', opacity: 0},
                                     show: {y: '0%', opacity: 1, transition: {duration: 0.8, ease: EASE}}
                                 }}>
                        {p}
                    </motion.span>
                </span>
            )))}
        </Comp>
    );
}

/**
 * Scroll-linked "grow into place": the block starts slightly smaller and inset, and reaches full
 * size as it scrolls toward the middle of the viewport (tied to scroll position, not a one-off tween).
 */
export function ScrollGrow({from = 0.86, className, children, ...rest}) {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const {scrollYProgress} = useScroll({target: ref, offset: ['start end', 'start 0.5']});
    const eased = useSpring(scrollYProgress, {stiffness: 120, damping: 30, restDelta: 0.001});
    const scale = useTransform(eased, [0, 1], [from, 1]);
    const opacity = useTransform(eased, [0, 0.35], [0.4, 1]);
    if (reduce) return <div ref={ref} className={className} {...rest}>{children}</div>;
    return (
        <motion.div ref={ref} className={className} style={{scale, opacity, transformOrigin: '50% 0%'}} {...rest}>
            {children}
        </motion.div>
    );
}

function ScrollWord({progress, range, children}) {
    const opacity = useTransform(progress, range, [0.16, 1]);
    return <motion.span className="wx-scroll-word" style={{opacity}}>{children}</motion.span>;
}

/**
 * Statement text whose words brighten one by one as the paragraph scrolls through the viewport.
 * Pass plain words as a string; inline elements (icons, accents) can be passed via `parts`.
 */
export function ScrollWords({as = 'p', className, parts}) {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const {scrollYProgress} = useScroll({target: ref, offset: ['start 0.85', 'end 0.45']});
    const Comp = as;
    const tokens = [];
    parts.forEach((part) => {
        if (typeof part === 'string') {
            part.split(/\s+/).filter(Boolean).forEach((w) => tokens.push(w));
        } else {
            tokens.push(part);
        }
    });
    if (reduce) {
        return <Comp ref={ref} className={className}>{tokens.map((t, i) => <React.Fragment key={i}>{t} </React.Fragment>)}</Comp>;
    }
    const n = tokens.length;
    return (
        <Comp ref={ref} className={className}>
            {tokens.map((t, i) => (
                <React.Fragment key={i}>
                    <ScrollWord progress={scrollYProgress} range={[i / n, Math.min(1, (i + 1.5) / n)]}>{t}</ScrollWord>
                    {' '}
                </React.Fragment>
            ))}
        </Comp>
    );
}

/** Route-level entrance animation. */
export function PageTransition({children}) {
    return (
        <motion.main
            initial={{opacity: 0, y: 14}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.55, ease: EASE}}
        >
            {children}
        </motion.main>
    );
}
